import {
  Box,
  Card,
  CardContent,
  alpha,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Avatar,
  Alert,
  Button,
  CircularProgress,
  Chip,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { theme } from "../../../../../theme";
import {
  Assignment as AssignmentIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { UserRole, Role } from "../UserManagement.interface";
import { useAuth } from "../../../../auth/hooks/useAuth";
import { userManagementService } from "../../../../../services/userManagement.service";
import { toast } from "react-toastify";
import { toLower } from "lodash";

interface IRolesMatrixProps {
  selectedLocationId: string;
  moduleKey: string;
  themeColor: string;
  mockUsers: Array<{
    _id: string;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    newPermissions?: any;
    newRoles?: any[];
  }>;
  userRoles: UserRole[];
  onRoleToggle: (userId: string, roleId: string) => void;
  accountingColors: any;
  // Add new props for saving functionality
  saving?: boolean;
  pendingChangesCount?: number;
  onSaveRoleChanges?: () => void;
}

export const RolesMatrix: React.FC<IRolesMatrixProps> = ({
  selectedLocationId,
  moduleKey,
  themeColor,
  mockUsers,
  userRoles,
  onRoleToggle,
  accountingColors,
  saving,
  pendingChangesCount,
  onSaveRoleChanges,
}) => {
  const { user } = useAuth();

  // Real-time roles state
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);

  // Track pending role changes locally (location-aware)
  const [pendingRoleChanges, setPendingRoleChanges] = useState<{
    [locationUserId: string]: {
      roleId: string | null; // null means no role assigned
      roleName: string;
    };
  }>({});

  // Helper function to create location-user key
  const getLocationUserKey = (userId: string, locationId: string): string => {
    return `${locationId}_${userId}`;
  };

  // Helper function to get pending changes for current location
  const getCurrentLocationPendingChanges = (): string[] => {
    return Object.keys(pendingRoleChanges).filter((key) =>
      key.startsWith(`${selectedLocationId}_`)
    );
  };

  // Helper function to get API module name
  const getAPIModuleName = (moduleKey: string): string => {
    const moduleMap: { [key: string]: string } = {
      hr: "HR",
      accounting: "Accounting",
      inventory: "Inventory",
      pos: "POS",
    };
    return moduleMap[moduleKey] || moduleKey;
  };

  // Fetch roles from API
  const fetchRoles = async () => {
    if (!user?.store_id) return;

    setLoading(true);
    try {
      const apiModuleName = toLower(getAPIModuleName(moduleKey));
      const response = await userManagementService.listRoles({
        module: apiModuleName,
        pageSize: 1000,
      });

      if (response.data?.data) {
        const rolesData = response.data.data.map((role: any) => ({
          ...role,
          id: role._id || role.id,
          name: role.role_name || role.name,
        }));
        setRoles(rolesData);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error("Failed to fetch roles");
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for user data
  const getUserDisplayName = (user: any): string => {
    const firstName = user.first_name || "";
    const lastName = user.last_name || "";
    return `${firstName} ${lastName}`.trim() || user.email || "Unknown User";
  };

  const getUserInitials = (user: any): string => {
    const firstName = user.first_name || "";
    const lastName = user.last_name || "";

    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    } else if (firstName) {
      return firstName.charAt(0).toUpperCase();
    } else if (lastName) {
      return lastName.charAt(0).toUpperCase();
    } else if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }

    return "?";
  };

  // Get user's current role for the selected module and location
  const getUserCurrentRole = (
    userId: string
  ): { roleId: string | null; roleName: string } => {
    // Check pending changes first (location-specific)
    const locationUserKey = getLocationUserKey(userId, selectedLocationId);
    if (pendingRoleChanges[locationUserKey]) {
      return {
        roleId: pendingRoleChanges[locationUserKey].roleId,
        roleName: pendingRoleChanges[locationUserKey].roleName,
      };
    }

    // Fall back to actual user data
    const targetUser = mockUsers.find((u) => u._id === userId);
    if (!targetUser?.newRoles) {
      return { roleId: null, roleName: "No Role" };
    }

    // Find role for current module and location
    const currentRoleAssignment = targetUser.newRoles.find(
      (roleData: any) =>
        roleData.module === moduleKey &&
        roleData.location_id === selectedLocationId
    );

    if (currentRoleAssignment) {
      const role = roles.find((r) => r.id === currentRoleAssignment.role_id);
      return {
        roleId: currentRoleAssignment.role_id,
        roleName: role?.name || "Unknown Role",
      };
    }

    return { roleId: null, roleName: "No Role" };
  };

  const hasRole = (userId: string, roleId: string) => {
    const currentRole = getUserCurrentRole(userId);
    return currentRole.roleId === roleId;
  };

  // Handle role toggle with local tracking (location-aware)
  const handleRoleToggle = (userId: string, roleId: string) => {
    if (!selectedLocationId || !user) return;

    const role = roles.find((r) => r.id === roleId);
    if (!role) return;

    const currentRole = getUserCurrentRole(userId);
    const locationUserKey = getLocationUserKey(userId, selectedLocationId);

    // Debug: Log the role toggle

    if (currentRole.roleId === roleId) {
      // Remove the role (unassign) - pass the same roleId so parent knows to remove it

      setPendingRoleChanges((prev) => ({
        ...prev,
        [locationUserKey]: {
          roleId: null,
          roleName: "No Role",
        },
      }));
      // Pass the same roleId to parent - parent will detect it's the same and remove it
      onRoleToggle(userId, roleId);
    } else {
      // Assign new role (this will replace any existing role for this location)

      setPendingRoleChanges((prev) => ({
        ...prev,
        [locationUserKey]: {
          roleId: roleId,
          roleName: role.name,
        },
      }));
      // Pass the new roleId to parent
      onRoleToggle(userId, roleId);
    }
  };

  // Clear pending changes for current location when location changes
  useEffect(() => {
    if (selectedLocationId) {
      setPendingRoleChanges((prev) => {
        const newPendingChanges = { ...prev };
        // Remove only the changes for the current location
        getCurrentLocationPendingChanges().forEach((key) => {
          delete newPendingChanges[key];
        });
        return newPendingChanges;
      });
    }
  }, [selectedLocationId, moduleKey]);

  // Fetch roles when component mounts or moduleKey changes
  useEffect(() => {
    fetchRoles();
  }, [moduleKey, user?.store_id]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {selectedLocationId ? (
        <Box>
          {/* Save Pending Changes Button */}
          {getCurrentLocationPendingChanges().length > 0 && (
            <Card sx={{ mb: 3, borderColor: "warning.main", borderWidth: 2 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "warning.main", mb: 0.5 }}
                    >
                      Unsaved Role Changes
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      You have {getCurrentLocationPendingChanges().length} user
                      {getCurrentLocationPendingChanges().length !== 1
                        ? "s"
                        : ""}{" "}
                      with pending role changes for this location
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        // Clear only changes for current location
                        setPendingRoleChanges((prev) => {
                          const newPendingChanges = { ...prev };
                          getCurrentLocationPendingChanges().forEach((key) => {
                            delete newPendingChanges[key];
                          });
                          return newPendingChanges;
                        });
                      }}
                      disabled={saving}
                      sx={{
                        borderColor: "warning.main",
                        color: "warning.main",
                        "&:hover": {
                          borderColor: "warning.dark",
                          backgroundColor: alpha(themeColor, 0.05),
                        },
                      }}
                    >
                      Clear Changes
                    </Button>
                    {onSaveRoleChanges && (
                      <Button
                        variant="contained"
                        startIcon={
                          saving ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <SaveIcon />
                          )
                        }
                        onClick={onSaveRoleChanges}
                        disabled={saving}
                        sx={{
                          bgcolor: "success.main",
                          color: "white",
                          fontWeight: 600,
                          "&:hover": {
                            bgcolor: "success.dark",
                          },
                        }}
                      >
                        {saving ? "Saving..." : "Save Role Changes"}
                      </Button>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent sx={{ p: 0 }}>
              <Box
                sx={{
                  p: 3,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  bgcolor: alpha(themeColor, 0.05),
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: themeColor }}
                >
                  Roles Matrix - {moduleKey.toUpperCase()}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Click on role cells to assign or unassign roles (one role per
                  user per location)
                </Typography>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table sx={{ minWidth: 600 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(themeColor, 0.1) }}>
                      <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>
                        User
                      </TableCell>
                      {roles.length > 0 ? (
                        roles.map((role) => (
                          <TableCell
                            key={role.id}
                            align="center"
                            sx={{ fontWeight: 600 }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                              }}
                            >
                              <AssignmentIcon
                                sx={{ fontSize: 16, color: themeColor }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: themeColor }}
                              >
                                {role.name}
                              </Typography>
                            </Box>
                          </TableCell>
                        ))
                      ) : (
                        <TableCell
                          align="center"
                          sx={{ fontStyle: "italic", color: "text.secondary" }}
                        >
                          No roles available
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockUsers.map((user: any) => (
                      <TableRow key={user._id}>
                        <TableCell>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: themeColor,
                                fontSize: "0.875rem",
                              }}
                            >
                              {getUserInitials(user)}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {getUserDisplayName(user)}
                                </Typography>
                                {pendingRoleChanges[
                                  getLocationUserKey(
                                    user._id,
                                    selectedLocationId
                                  )
                                ] && (
                                  <Chip
                                    label="Pending"
                                    size="small"
                                    sx={{
                                      height: 16,
                                      fontSize: "0.65rem",
                                      bgcolor: "warning.main",
                                      color: "white",
                                      fontWeight: 600,
                                    }}
                                  />
                                )}
                              </Stack>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {user.role}
                              </Typography>
                              {pendingRoleChanges[
                                getLocationUserKey(user._id, selectedLocationId)
                              ] && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "warning.main",
                                    display: "block",
                                    fontWeight: 600,
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  →{" "}
                                  {
                                    pendingRoleChanges[
                                      getLocationUserKey(
                                        user._id,
                                        selectedLocationId
                                      )
                                    ].roleName
                                  }
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        {roles.length > 0 ? (
                          roles.map((role: any) => {
                            const roleId = role.id;
                            const userHasRole = hasRole(user._id, roleId);
                            return (
                              <TableCell key={roleId} align="center">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    p: 1,
                                    borderRadius: 1,
                                    cursor: "pointer",
                                    bgcolor: userHasRole
                                      ? alpha(themeColor, 0.15)
                                      : "transparent",
                                    border: userHasRole
                                      ? `1px solid ${themeColor}`
                                      : "1px solid transparent",
                                    "&:hover": {
                                      bgcolor: alpha(themeColor, 0.1),
                                      border: `1px solid ${themeColor}`,
                                    },
                                  }}
                                  onClick={() =>
                                    handleRoleToggle(user._id, roleId)
                                  }
                                >
                                  {userHasRole ? (
                                    <CheckIcon
                                      sx={{ fontSize: 20, color: themeColor }}
                                    />
                                  ) : (
                                    <CloseIcon
                                      sx={{ fontSize: 20, color: "grey.400" }}
                                    />
                                  )}
                                </Box>
                              </TableCell>
                            );
                          })
                        ) : (
                          <TableCell
                            align="center"
                            sx={{
                              fontStyle: "italic",
                              color: "text.secondary",
                            }}
                          >
                            —
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      ) : (
        <Alert severity="info" sx={{ borderColor: accountingColors.info }}>
          Please select a location to view the roles matrix.
        </Alert>
      )}
    </Box>
  );
};
